/**
 * Range analytics contracts — slice 14, "campaign reach & date-range analytics".
 *
 * The demo this file has to make honest is one sentence: *pick two dates and see
 * what actually went out between them, and to how many distinct people.* Both
 * halves of that sentence have already been wrong once in this product, so
 * almost everything below is a shape chosen to make the old mistake fail to
 * compile rather than fail in review.
 *
 * REACH IS A UNIT, NOT A NUMBER
 * -----------------------------------------------------------------------------
 * Slice 2 paid for this. Reachability was once `count(*) FILTER (WHERE email IS
 * NOT NULL)` — a **row** count — and `bothChannels` was
 * `emailReachable + phoneReachable − contacts`. That expression is only
 * arithmetic if all three terms are the same unit, and the moment reach became
 * `count(distinct email)` while `contacts` stayed rows, it silently stopped
 * being arithmetic and stayed compiling. The first real data makes the gap
 * concrete: 531 rows carry an email address and only **526** of those addresses
 * are distinct. A segment advertising 531 and sending 526 looks broken to the
 * customer, and the difference was never visible in a type.
 *
 * So this file does not have a `reach: number`. It has three mutually
 * non-assignable one-field objects, each stamped with the unit it counted:
 *
 *   - {@link Reach} — `{ unit: 'distinct_addresses', addresses }`. What
 *     `count(distinct email)` or `count(distinct phone_e164)` returns. This is
 *     the only shape in the package the word *reach* may be printed beside, and
 *     {@link REACH_UNIT_LABEL} is the words the screen must use.
 *   - {@link RecipientReach} — `{ unit: 'distinct_recipients', recipients }`.
 *     Distinct people — since slice 58 the persons who own the recipients'
 *     `dedupe_key`s. **It is a separate type precisely so that it cannot be
 *     produced by adding two {@link Reach} values together**, which is slice 2's
 *     `bothChannels` bug restated: somebody carrying an email *and* a phone is
 *     two addresses and one person.
 *   - {@link RowCount} — `{ unit: 'rows', rows }`. The foil. `campaign_recipients`
 *     rows, campaigns in the range: real, useful figures that are not reach.
 *
 * The field names differ as well as the `unit` literals, so none of the three is
 * assignable to another in either direction, and `a.addresses + b.rows` cannot
 * be assigned back into any of them. That is the whole trick, and it is the same
 * trick slice 11 used to keep a directional engagement rate out of a KPI tile
 * and slice 12 used to keep a WhatsApp read *floor* out of a rate component.
 *
 * The rejected alternative was a branded number — `number & { __reach: true }`.
 * It reads cleaner and it does not work: TypeScript is happy to `+` two branded
 * numbers, so the one expression this file exists to refuse would still compile,
 * and the brand vanishes on the wire so the API and the front-end would disagree
 * about whether a JSON number was ever reach at all.
 *
 * A RANGE IS SLICE 13's WINDOW. IT IS NOT RE-SOLVED HERE
 * -----------------------------------------------------------------------------
 * {@link AnalyticsRange} **is** `ReportWindow` from `./report.js` — the same
 * type, aliased, not a lookalike: half-open `[from, to)` in a named IANA zone,
 * with the same 366-day cap and the same `to > from` refinement. `windowContains`
 * stays the one definition of the boundary, so the SQL, the screen's copy and
 * the suite proving a campaign on the boundary lands in exactly one bucket all
 * read the same rule. Nothing here re-exports slice 13's helpers, because two
 * `export *` modules exporting one name is a TS2308 at the package barrel;
 * builders import `describeReportWindow`, `windowContains` and
 * `reportWindowInstants` from `@wend/shared` directly.
 *
 * What *is* new is the step slice 13 never needed: a person picks two **calendar
 * dates**, and the later one is inclusive because that is what a date picker
 * means to the person clicking it. {@link resolveAnalyticsRange} turns
 * `2026-09-01 … 2026-09-30` in `Asia/Kolkata` into
 * `[2026-08-31T18:30Z, 2026-09-30T18:30Z)`. That inclusive-to-exclusive
 * conversion is the single most bug-prone line in the slice — get it wrong and
 * the last day of every range is missing, which nobody notices until they add
 * two months together — so it happens exactly once, here, and the resolved
 * window is handed to `reportWindowSchema` to be validated by slice 13's own
 * refinements rather than by a second copy of them.
 *
 * The zone is not optional and there is no UTC fallback. "Last month" computed
 * in UTC for a tenant in IST moves five and a half hours of sends into the wrong
 * bucket, and it does so silently; every partition-dating defect in slices 11
 * and 12 was this class. When `Intl` cannot resolve the zone,
 * {@link resolveAnalyticsRange} returns `null` and the caller shows nothing —
 * the same fail-closed choice `zoneOffsetMs` makes and for the same reason: a
 * range computed against the wrong clock is indistinguishable from a correct
 * one once it is on screen.
 *
 * A CAMPAIGN IS DATED BY ONE INSTANT, SO IT LANDS IN ONE BUCKET
 * -----------------------------------------------------------------------------
 * {@link campaignInRange} buckets a campaign by its own `sentAt`. A campaign
 * that starts at 23:50 on 30 September and is still materialising at 00:10 on
 * 1 October is entirely a September campaign. The rejected alternative — bucket
 * each campaign by the timestamps of the messages it produced — is right for
 * *events* (`delivery_events` is partitioned by each event's own timestamp,
 * deliberately, so a read arriving in December belongs to December) and wrong
 * for *campaigns*: it puts one campaign in two ranges, and the two halves of it
 * then get added together by a reader who has no way to know they overlap.
 *
 * EMAIL AND WHATSAPP KEEP THEIR OWN WORDS, AND THERE IS NO COMBINED FAILURE
 * -----------------------------------------------------------------------------
 * {@link channelAnalyticsSchema} is a discriminated union on `channel`, so
 * nothing can read a failure figure without narrowing first. The email branch
 * carries `bounces` and `complaints` and `notDelivered`'s five SES outcomes; the
 * WhatsApp branch carries `read`, a {@link WaReadRate} floor, and
 * `notDelivered`'s seven Meta failure classes. Both branches name a field
 * `notDelivered` and the two types are unrelated, so `analytics.notDelivered`
 * on the un-narrowed union does not compile. There is no bounce on WhatsApp and
 * no read receipt on email, and neither breakdown has a `total` member — that
 * was already settled in `./delivery.js` and `./whatsapp-delivery.js`, and this
 * file reuses both rather than restating either.
 *
 * The combined figure is where the type system earns its place.
 * {@link CROSS_CHANNEL_MEASURE_VALUES} is `['accepted', 'delivered']` and it is
 * an enum, not documentation: a {@link CrossChannelTotal} carries a `measure`
 * literal from that set, so **`{ measure: 'failed' }` is neither constructible
 * nor parseable**. A combined delivered column is defensible — "the provider
 * took it" and "it reached the device" mean the same thing on both channels, and
 * slices 5, 6 and 12 kept `accepted` and `delivered` apart on purpose so the gap
 * between them is visible. A combined failure column is not: a tenant reading
 * "412 failed" cleans their list; a tenant reading "1 unreachable number, 411
 * rate-limited, 3 hard bounces" sends again tomorrow and keeps 411 customers.
 * One number destroys the only distinction anybody can act on.
 *
 * {@link crossChannelTotal} takes the two channel objects and reads the field
 * itself rather than taking two numbers, so a caller cannot hand it two failure
 * counts under a `'delivered'` label. Widening the measure enum to a member that
 * is not a plain count on *both* channels fails to compile inside that builder,
 * at the two `Readonly<Record<CrossChannelMeasure, number>>` annotations that
 * exist for no other purpose.
 *
 * **A `CrossChannelTotal` is never the denominator of a rate.** Bounce and
 * complaint rates are drawn against thresholds AWS enforces on *email*, and
 * putting WhatsApp sends underneath one of them is the critical defect slice 10
 * shipped: a workspace sitting exactly on AWS's 5% line rendered as 1.55% behind
 * a green badge. `isBounceMeasured` in `./delivery.js` is still the predicate
 * that decides which channel may enter such a denominator, and nothing in this
 * file computes a rate from a cross-channel figure. The email branch is the only
 * shape here a bounce or complaint rate may be built from.
 *
 * ZERO AND ABSENT, AGAIN
 * -----------------------------------------------------------------------------
 * `email` and `whatsapp` on {@link analyticsReportSchema} are **nullable**, and
 * `null` means *this workspace does not send on that channel* — not zero. A
 * tenant with no Meta integration showing "0 delivered on WhatsApp" is the lie
 * this repository keeps re-learning, and `overviewRecentCampaignSchema` already
 * puts `null` in `delivered` on an unmeasured channel for the same reason. When
 * neither channel is measured there is no combined total either, which is why
 * {@link crossChannelTotal} returns `null` rather than a zero, and why
 * `measuredChannels` on the total is `.min(1)`: a total nothing contributed to
 * is not a total.
 *
 * Zero campaigns in the range is a different statement again, and it is a
 * sentence rather than a grid of zeros — {@link NO_CAMPAIGNS_IN_RANGE_NOTE}.
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT CARRY
 * -----------------------------------------------------------------------------
 * There is no open rate and no click rate on the email branch. Slice 11 built
 * them and they live in `./engagement.js` behind a per-tenant, off-by-default
 * switch, so on most tenants the honest answer is "not measured" rather than a
 * number — and a range report that renders 0% opened for a workspace with
 * tracking off is worse than one that does not mention opening at all. A builder
 * that wants them composes `EngagementRate` alongside this report; it is not
 * folded in, because folding it in would mean this schema had to carry a
 * "measurement is off" branch for a measurement it does not own.
 *
 * WHERE IT SITS
 * -----------------------------------------------------------------------------
 * A **leaf**. It imports `./report.js` (the window and its zoned arithmetic),
 * `./campaign.js` (`timeZoneSchema`, still the one place an IANA zone is
 * validated), `./delivery.js` and `./whatsapp-delivery.js` (the two failure
 * vocabularies), and nothing imports it back. That direction is not taste:
 * slices 8, 10, 12 and 13 each stepped near a cycle between two modules that
 * both build zod schemas at load, and it does not surface as a review comment —
 * it surfaces as `z.enum(undefined)` thrown at import, before a test runs.
 * Defining a second window, a second timezone schema or a second failure
 * breakdown here to avoid an import arrow would have been the worse trade every
 * time: two opinions about the same thing, one of which stops being maintained.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { channelSchema, type Channel } from '../enums.js';
import { timeZoneSchema } from './campaign.js';
import {
  EMPTY_BOUNCE_BREAKDOWN,
  EMPTY_NOT_DELIVERED_BREAKDOWN,
  bounceBreakdownSchema,
  notDeliveredBreakdownSchema,
} from './delivery.js';
import {
  MAX_REPORT_WINDOW_DAYS,
  reportWindowSchema,
  windowContains,
  zoneOffsetMs,
  zonedStartOf,
  type ReportWindow,
} from './report.js';
import {
  EMPTY_WA_FAILURE_BREAKDOWN,
  waFailureBreakdownSchema,
  waReadRate,
  waReadRateSchema,
} from './whatsapp-delivery.js';

const MS_PER_DAY = 86_400_000;

/* -------------------------------------------------------------------------- */
/* Units — the three counts this slice must never confuse                      */
/* -------------------------------------------------------------------------- */

/**
 * The marker on a distinct-address count. One member, and there will not be a
 * second: a figure called reach that is not `count(distinct <address>)` is not a
 * different flavour of reach, it is a different number wearing the word.
 */
export const REACH_UNIT = 'distinct_addresses' as const;

/** The words the screen must use beside a {@link Reach}. Not "contacts". */
export const REACH_UNIT_LABEL = 'distinct addresses';

/**
 * Why the number is smaller than the list, in the words a customer recognises.
 *
 * Shipped from the contract rather than typed into a component so the sentence
 * cannot drift away from the query: the same two figures — rows and distinct
 * addresses — are what {@link RowCount} and {@link Reach} carry.
 */
export const REACH_EXPLAINER =
  'Reach counts distinct addresses, not rows. A list that repeats an address reaches one person, not two, and sends one message, not two.';

/**
 * **A distinct-address count.** `count(distinct email)` or
 * `count(distinct phone_e164)` over the channel's own column — never `count(*)`,
 * never a `FILTER (WHERE … IS NOT NULL)` row count, and never the sum of two
 * channels' reach.
 *
 * A one-field object rather than a bare `number` so that a row count cannot be
 * passed where this belongs and this cannot be passed where a row count belongs.
 * Slice 2's header records what the bare number cost: 531 rows advertised, 526
 * messages sent, and no type anywhere that could tell the two apart.
 */
export const reachSchema = z
  .object({
    /** Always {@link REACH_UNIT}. The discriminator against {@link RowCount}. */
    unit: z.literal(REACH_UNIT),
    /** `count(distinct <channel address>)`. Addresses, not rows, not people. */
    addresses: z.number().int().min(0),
  })
  .strict();
export type Reach = z.infer<typeof reachSchema>;

/** The only supported way to make one. Clamps, so a bad query cannot render −3. */
export function reach(distinctAddresses: number): Reach {
  return { unit: REACH_UNIT, addresses: Math.max(0, Math.trunc(distinctAddresses)) };
}

/** A measured channel that reached nobody in the range. Honest: it was measured. */
export const EMPTY_REACH: Reach = { unit: REACH_UNIT, addresses: 0 };

/**
 * The marker on a distinct-recipient count. Separate from {@link REACH_UNIT}
 * because the two are separate questions and the answers differ.
 */
export const RECIPIENT_REACH_UNIT = 'distinct_recipients' as const;

/** The words the screen must use beside a {@link RecipientReach}. */
export const RECIPIENT_REACH_UNIT_LABEL = 'distinct recipients';

/** Why it is not the two channel figures added up. Printed beside the number. */
export const RECIPIENT_REACH_EXPLAINER =
  'Each person counted once across both channels, so somebody reached by email and by WhatsApp is one recipient here and two addresses above.';

/**
 * **A distinct-recipient count** — people: since slice 58 the distinct persons
 * who own the recipients' `dedupe_key`s (until then, the keys themselves).
 *
 * Its own type rather than a third {@link Reach} because the arithmetic that
 * produces it is not addition. `emailReach.addresses + whatsappReach.addresses`
 * double-counts everybody carrying both, which is exactly slice 2's
 * `bothChannels = emailReachable + phoneReachable − contacts` — an expression
 * that mixed distinct addresses with rows, compiled, and was wrong. Here the sum
 * does not typecheck into this shape at all: it is a bare `number`, and
 * {@link recipientReach} is the only constructor.
 *
 * That constructor still takes a number, so the discipline it enforces is
 * "somebody had to write this down deliberately", not "the compiler ran the
 * query". The query is the API's job, and its own test asserts the figure
 * against a literal.
 */
export const recipientReachSchema = z
  .object({
    /** Always {@link RECIPIENT_REACH_UNIT}. */
    unit: z.literal(RECIPIENT_REACH_UNIT),
    /** Distinct people — the owners of the recipients' dedupe keys, since slice 58. Not addresses. */
    recipients: z.number().int().min(0),
  })
  .strict();
export type RecipientReach = z.infer<typeof recipientReachSchema>;

/** The only supported way to make one. */
export function recipientReach(distinctRecipients: number): RecipientReach {
  return {
    unit: RECIPIENT_REACH_UNIT,
    recipients: Math.max(0, Math.trunc(distinctRecipients)),
  };
}

/** No campaigns in the range, or campaigns that materialised nobody. */
export const EMPTY_RECIPIENT_REACH: RecipientReach = {
  unit: RECIPIENT_REACH_UNIT,
  recipients: 0,
};

/** The marker on a plain row count. The foil the other two are defined against. */
export const ROW_COUNT_UNIT = 'rows' as const;

/** The words the screen must use beside a {@link RowCount}. */
export const ROW_COUNT_UNIT_LABEL = 'rows';

/**
 * **A row count.** `count(*)` over whatever rows the field says.
 *
 * Not deprecated and not a lesser number: how many recipient rows a campaign
 * materialised and how many campaigns went out are both real and both wanted.
 * It exists as a *type* so that it can sit in the same object as a {@link Reach}
 * — which is where the confusion has always happened — without either being
 * assignable to the other.
 */
export const rowCountSchema = z
  .object({
    /** Always {@link ROW_COUNT_UNIT}. The discriminator against {@link Reach}. */
    unit: z.literal(ROW_COUNT_UNIT),
    /** `count(*)`. Rows. Says nothing about how many people or addresses. */
    rows: z.number().int().min(0),
  })
  .strict();
export type RowCount = z.infer<typeof rowCountSchema>;

/** The only supported way to make one. */
export function rowCount(rows: number): RowCount {
  return { unit: ROW_COUNT_UNIT, rows: Math.max(0, Math.trunc(rows)) };
}

/** Zero rows. */
export const EMPTY_ROW_COUNT: RowCount = { unit: ROW_COUNT_UNIT, rows: 0 };

/* -------------------------------------------------------------------------- */
/* The range — slice 13's window, entered as two calendar dates                */
/* -------------------------------------------------------------------------- */

/**
 * The period the report covers. **This is slice 13's `ReportWindow`**, aliased
 * rather than redefined: half-open `[from, to)`, instants on the wire, plus the
 * named zone the boundaries were chosen in.
 *
 * Aliased and not wrapped, so every helper slice 13 already wrote applies
 * unchanged — `windowContains`, `reportWindowInstants`, `describeReportWindow`,
 * `reportWindowDurationDays`, and the 366-day cap. A wrapper type would have
 * meant a second `contains` written by hand, and a second `>=`/`<` is precisely
 * how a campaign on the boundary gets counted in both months.
 */
export const analyticsRangeSchema = reportWindowSchema;
export type AnalyticsRange = ReportWindow;

/**
 * A calendar date as a person types it: `YYYY-MM-DD`, no time, no zone.
 *
 * Distinct from `isoDateTimeSchema` on purpose. A date picker does not produce
 * an instant — `2026-09-30` is not a moment until somebody says *where* — and
 * accepting an instant here would let the front-end pick the zone by accident
 * (its own) while the report claims to have used the tenant's.
 *
 * The regex is not enough on its own: `2026-02-30` matches it and is not a day.
 * The refinement round-trips through `Date.UTC` so an impossible date is refused
 * at the contract rather than silently rolled forward into March.
 */
export const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'A date must be written YYYY-MM-DD')
  .refine((value) => parseCalendarDate(value) !== null, 'That is not a real date');
export type CalendarDate = z.infer<typeof calendarDateSchema>;

/** The parts of a `YYYY-MM-DD`, or `null` when it is not a real day. */
export function parseCalendarDate(
  value: string,
): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return null;
  const year = Number.parseInt(match[1] ?? '', 10);
  const month = Number.parseInt(match[2] ?? '', 10);
  const day = Number.parseInt(match[3] ?? '', 10);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  // Round-trip: Date.UTC rolls 2026-02-30 forward to 2026-03-02, so a value that
  // comes back different was never a day.
  const asUtc = new Date(Date.UTC(year, month - 1, day));
  if (
    asUtc.getUTCFullYear() !== year ||
    asUtc.getUTCMonth() !== month - 1 ||
    asUtc.getUTCDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

/**
 * The day after a calendar date, in calendar terms. `null` if the input is not
 * a real day.
 *
 * Calendar arithmetic rather than "add 86,400,000 milliseconds", because the
 * input has no zone and therefore no duration — and because the only caller is
 * turning an *inclusive* end date into an *exclusive* boundary, where the answer
 * must be "the next day" and not "24 hours later, whatever day that is".
 */
export function nextCalendarDate(value: string): CalendarDate | null {
  const parts = parseCalendarDate(value);
  if (parts === null) return null;
  const next = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + 1));
  const year = String(next.getUTCFullYear()).padStart(4, '0');
  const month = String(next.getUTCMonth() + 1).padStart(2, '0');
  const day = String(next.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The instant a calendar date began in a named zone, or `null` when the platform
 * cannot resolve the zone.
 *
 * Two passes through `zoneOffsetMs`, via local **noon** rather than local
 * midnight. Anchoring on noon is what makes this safe across a DST transition:
 * every real transition is at most a couple of hours and happens near midnight,
 * so an instant twelve hours from both of a day's boundaries is unambiguously
 * inside that day whichever offset was in force. Anchoring on midnight is the
 * version that silently lands on the previous day in a zone that springs
 * forward over it. `zonedStartOf` then snaps noon back to the day's first
 * instant using slice 13's own arithmetic — including its stated behaviour in a
 * zone where local midnight does not exist.
 *
 * **Noon-anchoring is necessary and was never sufficient, and the missing half
 * was slice 13's to fix rather than this file's.** Where clocks fall back
 * *through* midnight — America/Havana on 2026-11-01, Atlantic/Azores on
 * 2026-10-25 — local 00:00 happens twice and noon is in standard time, so the
 * old `zonedStartOf` snapped to the *second* midnight and the day's first
 * repeated hour fell outside its own day: a message accepted at
 * 2026-11-01T04:30Z was returned for October and by no November range. It now
 * takes the earliest instant that reads as the boundary, so this function needs
 * no opinion of its own about ambiguity — which is the point of borrowing it.
 *
 * Fails closed, like everything downstream of `zoneOffsetMs`: a range computed
 * against the wrong clock produces a report that is confidently wrong, and
 * nobody looking at it can tell.
 */
export function zonedCalendarDayStart(value: string, timezone: string): Date | null {
  const parts = parseCalendarDate(value);
  if (parts === null) return null;
  const noonAsIfUtc = Date.UTC(parts.year, parts.month - 1, parts.day, 12);
  const offset = zoneOffsetMs(new Date(noonAsIfUtc), timezone);
  if (offset === null) return null;
  return zonedStartOf(new Date(noonAsIfUtc - offset), timezone, 'day');
}

/**
 * What the screen sends: two calendar dates and the zone they mean something in.
 *
 * **`toDate` is inclusive.** A person picking 1 September to 30 September means
 * the whole of the 30th, and a contract that quietly treated the end date as
 * exclusive would drop the busiest day of most ranges — the last one — with no
 * symptom other than a total that is slightly too small.
 * {@link resolveAnalyticsRange} is where inclusive becomes exclusive, once.
 *
 * `timezone` has no default. `DEFAULT_TIME_ZONE` exists in `./campaign.js` and
 * is deliberately not reached for: defaulting here would mean a request that
 * forgot the field still produced a plausible report, bucketed against a clock
 * nobody chose. The caller must say which zone it means.
 *
 * **What the superseded sentence claimed — "the caller knows the tenant's zone"
 * — is not true today, and the refusal to default here does not make it true.**
 * There is no timezone column on `tenants` and no tenant zone in any contract,
 * so the one caller there is, `AnalyticsPage`, answers with
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`: the **viewer's device**.
 * Two people in two countries therefore read different periods off the same two
 * dates, and the screen says so in words (`AnalyticsCopy`) because it cannot yet
 * do anything better. Keeping the field required is still right — a request that
 * forgot it would be worse — but a reader should not take it as evidence that a
 * workspace zone exists somewhere upstream. When one does, it is this field it
 * fills.
 *
 * The length cap reads {@link MAX_REPORT_WINDOW_DAYS} rather than declaring a
 * number, so there is one such limit in the package. It is checked twice — here
 * on calendar days, for an error message a form can render, and again on the
 * resolved instants by `reportWindowSchema` inside {@link resolveAnalyticsRange}.
 * Two checks, one constant, and the second is a backstop rather than a second
 * policy.
 *
 * **Which took a fix to become true.** A calendar day is not always 24 hours, so
 * 366 calendar days containing a fall-back and no spring-forward resolve to 366
 * days and one hour: this schema said yes, the millisecond check said no, and
 * the only failure channel `resolveAnalyticsRange` has is `null` — which the
 * controller renders as *"those dates could not be resolved in
 * America/New_York"*, blaming the zone for a range that resolved perfectly and
 * saying nothing about length. `reportWindowSchema` now carries one zone shift
 * of slack for exactly this, so the backstop can only fire on a window this
 * schema would also refuse.
 */
export const analyticsRangeRequestSchema = z
  .object({
    /** First day included, in `timezone`. */
    fromDate: calendarDateSchema,
    /** Last day included, in `timezone`. **Inclusive** — see above. */
    toDate: calendarDateSchema,
    /** The zone both dates are read in, and the zone the report prints. */
    timezone: timeZoneSchema,
  })
  .strict()
  .superRefine((request, ctx) => {
    const from = parseCalendarDate(request.fromDate);
    const to = parseCalendarDate(request.toDate);
    if (from === null || to === null) return;

    const fromUtc = Date.UTC(from.year, from.month - 1, from.day);
    const toUtc = Date.UTC(to.year, to.month - 1, to.day);
    if (toUtc < fromUtc) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toDate'],
        message: 'The last day of the range cannot be before the first',
      });
      return;
    }
    // Inclusive, so a single day is one day rather than zero.
    const days = (toUtc - fromUtc) / MS_PER_DAY + 1;
    if (days > MAX_REPORT_WINDOW_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['toDate'],
        message: `A range covers at most ${MAX_REPORT_WINDOW_DAYS} days; ask for a shorter period`,
      });
    }
  });
export type AnalyticsRangeRequest = z.infer<typeof analyticsRangeRequestSchema>;

/**
 * Two calendar dates and a zone become one half-open window, or `null`.
 *
 * `null` covers both failures a caller must not paper over: the platform could
 * not resolve the zone, or the resolved instants did not satisfy slice 13's own
 * window rules. Neither has a safe fallback — UTC is the wrong clock and a
 * clamped window is a report that answers a question nobody asked — so the
 * caller renders the failure instead.
 *
 * The upper bound is the start of the day **after** `toDate`, which is what
 * makes `[from, to)` and an inclusive date picker agree. Derived by calendar
 * arithmetic and then resolved in the zone, not by adding 24 hours to the lower
 * bound: adding a day of milliseconds is wrong twice a year, and it is wrong in
 * the direction that leaves a gap or an overlap between two adjacent ranges, so
 * a campaign is reported twice or never. Slice 13's `resolveReportWindow` avoids
 * the same trap the same way and its header argues it at length.
 */
export function resolveAnalyticsRange(request: AnalyticsRangeRequest): AnalyticsRange | null {
  const dayAfter = nextCalendarDate(request.toDate);
  if (dayAfter === null) return null;

  const from = zonedCalendarDayStart(request.fromDate, request.timezone);
  const to = zonedCalendarDayStart(dayAfter, request.timezone);
  if (from === null || to === null) return null;

  const parsed = analyticsRangeSchema.safeParse({
    from: from.toISOString(),
    to: to.toISOString(),
    timezone: request.timezone,
  });
  return parsed.success ? parsed.data : null;
}

/**
 * Whether a campaign belongs to this range. **`from <= sentAt < to`.**
 *
 * Delegates to `windowContains` rather than comparing here, so there is exactly
 * one implementation of the boundary in the package and a campaign sent at the
 * instant a range ends lands in the next range and only the next range.
 *
 * The instant is the campaign's own `sentAt`, and that single choice is what
 * makes "exactly one bucket" structural rather than tested: a campaign has one
 * send instant, so it has one range. Bucketing by the timestamps of the messages
 * it produced would split a campaign that ran across midnight into two ranges
 * whose halves a reader would then add together.
 */
export function campaignInRange(range: AnalyticsRange, sentAt: string | Date): boolean {
  const instant = typeof sentAt === 'string' ? new Date(sentAt) : sentAt;
  if (Number.isNaN(instant.getTime())) return false;
  return windowContains(range, instant);
}

/* -------------------------------------------------------------------------- */
/* Per-channel analytics — two vocabularies that never merge                   */
/* -------------------------------------------------------------------------- */

/**
 * What went out on **email** in the range, in email's own words.
 *
 * **Every figure here is a count of distinct messages, and where each one is
 * counted from is not the same question.** SES and SNS both re-notify, a
 * redelivery arrives under a fresh notification id and passes the replay guard,
 * and slices 10, 11 and 12 each shipped a version of the resulting double count.
 * So: anything counted over `delivery_events` is `countDistinct(message_id)`,
 * **never `count(*)`** — that is the rule, and `complaints`, `bounces` and
 * `notDelivered` below are read that way.
 *
 * `accepted` and `delivered` are not read from events at all. They come from
 * `messages`, whose primary key is one row per message: `accepted` is
 * `count(*) filter (where sent_at is not null)` and `delivered` is the settled
 * `messages.status`. That satisfies the distinct-message rule by the table's own
 * key, and it is deliberate rather than incidental —
 * `apps/api/src/modules/analytics/analytics-volumes.ts` argues it at length and
 * this paragraph is the same decision stated where both front-ends can read it.
 * **The superseded version of these lines said `countDistinct(message_id)` over
 * `delivery_events` for both**, which is the reading slice 12 rejected: nothing
 * stops one message carrying a `delivery` event and a later `failed` one — SES
 * emits an asynchronous permanent bounce hours after a delivery, and the Meta
 * ingest writes every understood status as an event before consulting
 * `isTerminalMessageStatus` — so counted from events, one message lands in two
 * mutually exclusive buckets and `delivered + failed` exceeds `accepted`. An
 * exporter or a second screen written from the old sentence would have
 * disagreed with this one about the same campaign, which is the defect
 * `campaign-whatsapp-results.ts` records by name (the Overview said 9 and the
 * campaign panel said 10).
 *
 * `bounces` and `notDelivered` overlap deliberately, exactly as they do on
 * `campaignResultsSchema`: one splits the bounced *messages* hard from soft, the
 * other splits the *events* that stopped delivery, and they disagree in the
 * informative case — a rejection or a rendering failure never became a bounce.
 * Neither has a total and neither is summed here.
 *
 * No open or click figure: see this file's header. They are measured only where
 * a tenant switched tracking on, and a zero would read as "nobody opened it".
 */
export const emailAnalyticsSchema = z
  .object({
    channel: z.literal('email'),
    /** Distinct email addresses messaged in the range. Not rows. */
    reach: reachSchema,
    /** `campaign_recipients` rows materialised. Rows, and says so. */
    recipientRows: rowCountSchema,
    /** Distinct messages SES took. `messages` rows with a `sent_at`. */
    accepted: z.number().int().min(0),
    /** Distinct messages whose settled `messages.status` is `delivered`. Not accepted. */
    delivered: z.number().int().min(0),
    /** Distinct messages that drew a complaint. `countDistinct(message_id)` over events. */
    complaints: z.number().int().min(0),
    /** Hard, soft and undetermined, kept apart. No total. */
    bounces: bounceBreakdownSchema,
    /** The five SES not-delivered outcomes, kept apart. No total. */
    notDelivered: notDeliveredBreakdownSchema,
  })
  .strict();
export type EmailAnalytics = z.infer<typeof emailAnalyticsSchema>;

/**
 * What went out on **WhatsApp** in the range, in WhatsApp's own words.
 *
 * `read` and `readRate` have no email counterpart and never will, and `failed`
 * has no email counterpart either — not because email cannot fail, but because
 * on email "failed" is five different events with five different owners and
 * collapsing them is the defect `notDeliveredBreakdownSchema` was shaped to
 * refuse. Meta reports one `failed` status carrying an error code, so a single
 * scalar is the honest shape *here* and would be a lie *there*. The asymmetry is
 * the point of keeping the two vocabularies apart.
 *
 * `readRate` is a {@link WaReadRate} — a **floor**, not a rate. Everyone with
 * read receipts switched off is missing from the numerator and can never appear
 * in it. Reusing slice 12's type rather than a percentage means no component can
 * render this figure with the authority of a bounce rate, and no component
 * written for an email open rate can be handed it either.
 */
export const whatsappAnalyticsSchema = z
  .object({
    channel: z.literal('whatsapp'),
    /** Distinct `phone_e164` values messaged in the range. Not rows. */
    reach: reachSchema,
    /** `campaign_recipients` rows materialised. Rows, and says so. */
    recipientRows: rowCountSchema,
    /** Distinct messages the Graph API took. `messages` rows with a `sent_at`. */
    accepted: z.number().int().min(0),
    /** Distinct messages whose settled status is `delivered`. The status that spends a credit. */
    delivered: z.number().int().min(0),
    /**
     * Distinct messages marked read. A floor — see {@link WaReadRate}.
     *
     * The one figure here that *is* `countDistinct(message_id)` over
     * `delivery_events`: a read moves no status, so there is nothing on
     * `messages` to count. See this file's note on `emailAnalyticsSchema`.
     */
    read: z.number().int().min(0),
    /** Distinct messages whose settled status is `failed`. Split by `notDelivered`. */
    failed: z.number().int().min(0),
    /** The seven Meta failure classes, kept apart. No total. */
    notDelivered: waFailureBreakdownSchema,
    /** Reads over delivered, or `null` below the floor. Never an exact rate. */
    readRate: waReadRateSchema,
  })
  .strict();
export type WhatsAppAnalytics = z.infer<typeof whatsappAnalyticsSchema>;

/**
 * One channel's half of the report.
 *
 * A discriminated union rather than an object with optional email-ish and
 * WhatsApp-ish fields, so a component must narrow on `channel` before it can
 * read a failure figure at all. Both branches name a field `notDelivered` and
 * the two types are unrelated, which means `analytics.notDelivered` on the
 * un-narrowed union does not compile — that is the compiler saying, in the only
 * language it has, that there is no such thing as a cross-channel failure.
 */
export const channelAnalyticsSchema = z.discriminatedUnion('channel', [
  emailAnalyticsSchema,
  whatsappAnalyticsSchema,
]);
export type ChannelAnalytics = z.infer<typeof channelAnalyticsSchema>;

/** A measured email channel that sent nothing in the range. */
export function emptyEmailAnalytics(): EmailAnalytics {
  return {
    channel: 'email',
    reach: EMPTY_REACH,
    recipientRows: EMPTY_ROW_COUNT,
    accepted: 0,
    delivered: 0,
    complaints: 0,
    bounces: { ...EMPTY_BOUNCE_BREAKDOWN },
    notDelivered: { ...EMPTY_NOT_DELIVERED_BREAKDOWN },
  };
}

/** A measured WhatsApp channel that sent nothing in the range. */
export function emptyWhatsAppAnalytics(): WhatsAppAnalytics {
  return {
    channel: 'whatsapp',
    reach: EMPTY_REACH,
    recipientRows: EMPTY_ROW_COUNT,
    accepted: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    notDelivered: { ...EMPTY_WA_FAILURE_BREAKDOWN },
    readRate: waReadRate({ numerator: 0, denominator: 0 }),
  };
}

/* -------------------------------------------------------------------------- */
/* The combined figure — and the one that cannot exist                         */
/* -------------------------------------------------------------------------- */

/**
 * The measures that mean the same thing on both channels, and therefore the only
 * measures a combined figure may be built for.
 *
 * **`'failed'` is absent and its absence is enforced, not documented.** A
 * {@link CrossChannelTotal} carries a `measure` from this enum, so a combined
 * failure total is neither constructible in TypeScript nor parseable from JSON:
 * a component cannot build one by accident and an API cannot return one by
 * mistake. That is the same move slice 11 made when it put `threshold` on an
 * exact rate and withheld it from a directional one, and slice 12 made when it
 * gave a WhatsApp read figure the literal `'floor'`.
 *
 *   - `accepted` — the provider took the message. SES accepting and the Graph
 *     API accepting are the same event with two names, and both channels already
 *     record it under this word.
 *   - `delivered` — it reached the recipient's mail server or device. Slices 5,
 *     6 and 12 kept this apart from `accepted` precisely so the gap is visible,
 *     and the gap is meaningful per channel and in total.
 *
 * There is no third member and adding one is not a small change: it has to name
 * a plain count that exists on **both** branches of
 * {@link channelAnalyticsSchema}, or {@link crossChannelTotal} stops compiling.
 * `complaints` and `read` fail that test, which is correct — neither has a
 * counterpart on the other channel — and `notDelivered` fails it because the two
 * breakdowns are unrelated types, which is the whole argument of this slice
 * expressed as a compiler error.
 */
export const CROSS_CHANNEL_MEASURE_VALUES = ['accepted', 'delivered'] as const;
export const crossChannelMeasureSchema = z.enum(CROSS_CHANNEL_MEASURE_VALUES);
export type CrossChannelMeasure = z.infer<typeof crossChannelMeasureSchema>;

/** How each combined measure is labelled, in words a reader recognises. */
export const CROSS_CHANNEL_MEASURE_LABELS = {
  accepted: 'Accepted by the provider',
  delivered: 'Delivered',
} as const satisfies Readonly<Record<CrossChannelMeasure, string>>;

/**
 * The sentence the screen shows where a reader expects a combined failure
 * column and does not find one.
 *
 * Copy, in the contract, because the omission is only honest if it is explained.
 * An unexplained gap reads as an oversight and invites the next person to fill
 * it in.
 */
export const NO_CROSS_CHANNEL_FAILURE_NOTE =
  'Failures are shown per channel and are not added together. An email that hard bounced and a WhatsApp message a rate limit stopped need opposite responses — clean the address off the list, or send again tomorrow — and one combined number would hide which is which.';

/**
 * A figure summed across the channels that are actually measured.
 *
 * `email` and `whatsapp` are nullable and `null` means **this workspace does not
 * send on that channel** — not zero. `measuredChannels` names the channels that
 * are in `total`, so the screen can say "email only" rather than implying a
 * WhatsApp figure of nothing, and it is `.min(1)` because a total nothing
 * contributed to is not a total; that case is a `null`
 * {@link CrossChannelTotal}, produced by {@link crossChannelTotal}.
 *
 * **Never a denominator.** AWS's bounce and complaint thresholds measure email,
 * and slice 10 shipped a critical where a workspace on AWS's 5% line rendered as
 * 1.55% because WhatsApp sends were underneath it. Rates are built from
 * {@link EmailAnalytics} alone, gated by `isBounceMeasured` in `./delivery.js`.
 */
export const crossChannelTotalSchema = z
  .object({
    /** Which figure this is. No failure member exists — see the enum. */
    measure: crossChannelMeasureSchema,
    /** Email's contribution, or `null` when this workspace does not send email. */
    email: z.number().int().min(0).nullable(),
    /** WhatsApp's contribution, or `null` when there is no WhatsApp here. */
    whatsapp: z.number().int().min(0).nullable(),
    /** The sum over `measuredChannels`, and nothing else. */
    total: z.number().int().min(0),
    /** The channels behind `total`. Never empty. */
    measuredChannels: z.array(channelSchema).min(1),
  })
  .strict();
export type CrossChannelTotal = z.infer<typeof crossChannelTotalSchema>;

/**
 * The only supported way to build one. `null` when neither channel is measured.
 *
 * It takes the two channel objects rather than two numbers on purpose: a caller
 * handed a `(measure, a, b)` signature can pass a failure count under a
 * `'delivered'` label and nothing notices. Here the builder reads the field
 * itself, from the measure, so the label and the numbers cannot disagree.
 *
 * The two local annotations below are the compile-time guard on
 * {@link CROSS_CHANNEL_MEASURE_VALUES} and exist for no other purpose: a measure
 * added there that is not a plain count on **both** branches makes these
 * assignments fail, and the error lands here rather than on a screen.
 */
export function crossChannelTotal(
  measure: CrossChannelMeasure,
  email: EmailAnalytics | null,
  whatsapp: WhatsAppAnalytics | null,
): CrossChannelTotal | null {
  if (email === null && whatsapp === null) return null;

  const emailCounts: Readonly<Record<CrossChannelMeasure, number>> | null = email;
  const whatsappCounts: Readonly<Record<CrossChannelMeasure, number>> | null = whatsapp;

  const emailCount = emailCounts === null ? null : emailCounts[measure];
  const whatsappCount = whatsappCounts === null ? null : whatsappCounts[measure];

  const measuredChannels: Channel[] = [];
  if (emailCount !== null) measuredChannels.push('email');
  if (whatsappCount !== null) measuredChannels.push('whatsapp');

  return {
    measure,
    email: emailCount,
    whatsapp: whatsappCount,
    total: (emailCount ?? 0) + (whatsappCount ?? 0),
    measuredChannels,
  };
}

/* -------------------------------------------------------------------------- */
/* The report                                                                  */
/* -------------------------------------------------------------------------- */

/** Shown instead of a grid of zeros when nothing went out in the range. */
export const NO_CAMPAIGNS_IN_RANGE_NOTE =
  'No campaigns went out in this period. That is different from a campaign that reached nobody.';

/**
 * Shown where a channel's figures would be, when the workspace does not send on
 * that channel at all. Never a zero, never a dash.
 */
export const CHANNEL_NOT_SENDING_NOTES = {
  email:
    'This workspace has no email sending set up, so there is nothing to report for email. That is not the same as an email campaign that reached nobody.',
  whatsapp:
    'This workspace has no WhatsApp integration, so there is nothing to report for WhatsApp. That is not the same as a WhatsApp campaign that reached nobody.',
} as const satisfies Readonly<Record<Channel, string>>;

/**
 * One campaign inside the range.
 *
 * `sentAt` is carried, not just used for bucketing, because a reader looking at
 * a list that straddles a boundary needs to see which side each campaign fell
 * on and why. `analytics` is the discriminated union, so a row's channel and its
 * vocabulary cannot come apart — there is no `channel` field beside it to
 * disagree with the one inside it.
 */
export const campaignAnalyticsSchema = z
  .object({
    campaignId: uuidSchema,
    /** The campaign's name as the tenant wrote it. */
    name: z.string().min(1),
    /**
     * The instant this campaign was sent. **The one instant it is bucketed by**
     * — see {@link campaignInRange}.
     */
    sentAt: isoDateTimeSchema,
    /** Reach and outcomes, in this campaign's channel's own vocabulary. */
    analytics: channelAnalyticsSchema,
  })
  .strict();
export type CampaignAnalytics = z.infer<typeof campaignAnalyticsSchema>;

/**
 * **What the range screen and any export read.** Computed on read, never stored.
 *
 * `rangeLabel` is carried on the wire rather than formatted by the component,
 * for the reason `overviewRateKpi` carries its band: the API's idea of what
 * period this covers and the screen's idea of it cannot then come apart, and the
 * same string goes into an export where there is no component at all. It is
 * `describeReportWindow(range)` — both boundaries, the zone named, and the words
 * *up to but not including* rather than a bracket four people per organisation
 * read correctly.
 *
 * `recipientReach` is the headline and it is **not** `email.reach.addresses +
 * whatsapp.reach.addresses`. It is a single `count(distinct coalesce(email,
 * phone_e164))` over the recipients of every campaign in the range, so somebody
 * reachable both ways is one person here and two addresses in the per-channel
 * figures. Its type is what stops the sum ever being assigned into it.
 *
 * `campaignCount` is a {@link RowCount} beside `campaigns`, a list of the same
 * things, which looks redundant and is not: the list is the page the reader is
 * looking at and the count is what the range holds. Where they differ, the list
 * is truncated — and a screen that derives "23 campaigns" from `campaigns.length`
 * says 25 when the range holds 400.
 *
 * `email` and `whatsapp` are `null` when this workspace does not send on that
 * channel. `accepted` and `delivered` are `null` only when both channels are.
 */
export const analyticsReportSchema = z
  .object({
    /** Half-open `[from, to)` in a named zone. The same window slice 13 defined. */
    range: analyticsRangeSchema,
    /** The window as a sentence, with the zone named. Printed on screen and in exports. */
    rangeLabel: z.string().min(1),
    /** When this was computed. It is a read, not a snapshot, and says so. */
    generatedAt: isoDateTimeSchema,
    /** Distinct people reached across both channels. Never a sum of the two below. */
    recipientReach: recipientReachSchema,
    /** How many campaigns went out in the range. Rows, deliberately. */
    campaignCount: rowCountSchema,
    /** Email's reach and outcomes, or `null` when this workspace does not send email. */
    email: emailAnalyticsSchema.nullable(),
    /** WhatsApp's reach and outcomes, or `null` when there is no WhatsApp here. */
    whatsapp: whatsappAnalyticsSchema.nullable(),
    /** Accepted, across the measured channels. `null` when none is measured. */
    accepted: crossChannelTotalSchema.nullable(),
    /** Delivered, across the measured channels. `null` when none is measured. */
    delivered: crossChannelTotalSchema.nullable(),
    /** The campaigns themselves, newest first. May be shorter than `campaignCount`. */
    campaigns: z.array(campaignAnalyticsSchema),
  })
  .strict();
export type AnalyticsReport = z.infer<typeof analyticsReportSchema>;

/** `GET /analytics/range`. Wrapped, like every other read in this package. */
export const analyticsReportResponseSchema = z.object({ report: analyticsReportSchema });
export type AnalyticsReportResponse = z.infer<typeof analyticsReportResponseSchema>;
